// src/pages/markets/board/api/scoreBatcher.ts

import type { QueryClient } from '@tanstack/react-query';
import { fetchBoardScores, MAX_EVENT_IDS } from './boardApi';
import { boardKeys } from './boardKeys';
import type {
    BoardEventsResponse,
    BoardScore,
    BoardScoresResponse,
    EventConvictionIndex,
} from './boardTypes';

export interface ScoreEntry {
    scores: BoardScore[];
    index: EventConvictionIndex | null;
}

export const EMPTY_SCORE_ENTRY: ScoreEntry = { scores: [], index: null };

// A microtask is too early: react-query starts each card's fetch in its own
// task, so a microtask flush degenerates to one request per (event, group).
// A short window catches a whole mount burst or tick refetch in one request.
const FLUSH_WINDOW_MS = 15;

interface Pending {
    eventId: string;
    group: string;
    resolve: (entry: ScoreEntry) => void;
    reject: (err: unknown) => void;
}

function pairKey(eventId: string, group: string): string {
    return `${eventId}|${group}`;
}

function sliceIndex(
    eventId: string,
    group: string,
    full: EventConvictionIndex | undefined,
    scores: BoardScore[],
): EventConvictionIndex | null {
    if (scores.length === 0) return null;
    const entry = full?.by_group?.find(g => g.group === group);
    const primary = full?.primary_market_by_group?.[group];
    let latest = full?.latest_ts ?? scores[0].ts;
    for (const s of scores) if (s.ts > latest) latest = s.ts;
    return {
        event_id: eventId,
        score_count: scores.length,
        markets_with_scores: new Set(scores.map(s => s.market_id)).size,
        latest_ts: latest,
        by_group: entry ? [entry] : [],
        primary_market_by_group: primary ? { [group]: primary } : {},
    };
}

export function entriesFromResponse(
    eventIds: readonly string[],
    groups: readonly string[],
    scores: readonly BoardScore[],
    index: readonly EventConvictionIndex[],
): Map<string, ScoreEntry> {
    const byPair = new Map<string, BoardScore[]>();
    for (const s of scores) {
        const k = pairKey(s.event_id, s.group);
        const list = byPair.get(k);
        if (list) list.push(s);
        else byPair.set(k, [s]);
    }
    const idxById = new Map(index.map(i => [i.event_id, i]));

    const out = new Map<string, ScoreEntry>();
    for (const id of eventIds) {
        for (const g of groups) {
            const k = pairKey(id, g);
            const list = byPair.get(k) ?? [];
            out.set(k, {
                scores: list,
                index: sliceIndex(id, g, idxById.get(id), list),
            });
        }
    }
    return out;
}

export function seedScoreCache(
    qc: QueryClient,
    page: BoardEventsResponse,
    groups: readonly string[],
): void {
    const ids = page.events.map(e => e.event_id);
    const entries = entriesFromResponse(ids, groups, page.scores, page.index);
    for (const [k, entry] of entries) {
        const sep = k.indexOf('|');
        qc.setQueryData<ScoreEntry>(boardKeys.score(k.slice(0, sep), k.slice(sep + 1)), entry);
    }
}

class ScoreBatcher {
    private pending = new Map<string, Pending[]>();
    private timer: ReturnType<typeof setTimeout> | null = null;

    constructor(private readonly qc: QueryClient) { }

    load(eventId: string, group: string): Promise<ScoreEntry> {
        return new Promise<ScoreEntry>((resolve, reject) => {
            const k = pairKey(eventId, group);
            const list = this.pending.get(k);
            const p: Pending = { eventId, group, resolve, reject };
            if (list) list.push(p);
            else this.pending.set(k, [p]);
            if (this.timer === null) {
                this.timer = setTimeout(() => void this.flush(), FLUSH_WINDOW_MS);
            }
        });
    }

    private async flush(): Promise<void> {
        this.timer = null;
        const batch = this.pending;
        this.pending = new Map();
        if (batch.size === 0) return;

        const ids = [...new Set([...batch.values()].map(l => l[0].eventId))];
        const groups = [...new Set([...batch.values()].map(l => l[0].group))];

        const chunks: string[][] = [];
        for (let i = 0; i < ids.length; i += MAX_EVENT_IDS) {
            chunks.push(ids.slice(i, i + MAX_EVENT_IDS));
        }

        try {
            const responses: BoardScoresResponse[] = await Promise.all(
                chunks.map(chunk => fetchBoardScores(chunk, groups)),
            );
            const scores = responses.flatMap(r => r.scores);
            const index = responses.flatMap(r => r.index);
            const entries = entriesFromResponse(ids, groups, scores, index);

            for (const [k, entry] of entries) {
                const waiting = batch.get(k);
                if (waiting) {
                    for (const p of waiting) p.resolve(entry);
                } else {
                    const sep = k.indexOf('|');
                    this.qc.setQueryData<ScoreEntry>(
                        boardKeys.score(k.slice(0, sep), k.slice(sep + 1)),
                        entry,
                    );
                }
            }
        } catch (err) {
            for (const waiting of batch.values()) {
                for (const p of waiting) p.reject(err);
            }
        }
    }
}

const batchers = new WeakMap<QueryClient, ScoreBatcher>();

export function getScoreBatcher(qc: QueryClient): ScoreBatcher {
    let b = batchers.get(qc);
    if (!b) {
        b = new ScoreBatcher(qc);
        batchers.set(qc, b);
    }
    return b;
}